import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '@/lib/api-client';
import { useAuthStore, type AuthUser } from '@/lib/auth-store';
import type { Role } from '@weremote/shared';

interface LoginResponse {
  accessToken: string;
  refreshToken: string;
  expiresIn: string;
  user: AuthUser;
}

export function useLogin() {
  const setSession = useAuthStore((s) => s.setSession);

  return useMutation({
    mutationFn: async (input: { email: string; password: string; orgSlug: string }) => {
      // We set the org slug into the store BEFORE the request so the
      // X-Org-Slug header is sent (otherwise the API can't resolve tenant).
      useAuthStore.setState({ orgSlug: input.orgSlug });
      try {
        const res = await api.post<LoginResponse>(
          '/auth/login',
          { email: input.email, password: input.password },
          { noAuth: true },
        );
        setSession({
          accessToken: res.accessToken,
          refreshToken: res.refreshToken,
          user: res.user,
          orgSlug: input.orgSlug,
        });
        return res;
      } catch (err) {
        // Roll back the speculative slug update if login failed
        useAuthStore.setState({ orgSlug: null });
        throw err;
      }
    },
  });
}

export function useLogout() {
  const clear = useAuthStore((s) => s.clear);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      try {
        await api.post('/auth/logout');
      } catch {
        // Ignore logout errors — we clear locally regardless
      }
    },
    onSettled: () => {
      clear();
      queryClient.clear();
    },
  });
}

interface MeResponse {
  user: AuthUser & {
    totpEnabled: boolean;
    lastLoginAt: string | null;
    org: { id: string; slug: string; name: string };
  };
}

export function useMe() {
  const accessToken = useAuthStore((s) => s.accessToken);

  return useQuery({
    queryKey: ['auth', 'me'],
    queryFn: () => api.get<MeResponse>('/auth/me'),
    enabled: !!accessToken,
    retry: (failureCount, error) => {
      if (error instanceof ApiError && error.status === 401) return false;
      return failureCount < 2;
    },
  });
}

export type { Role };
